import { z } from "zod"

// Внутренний баланс покупателя, леджер, пополнения и реквизиты ЮЛ для оплаты по
// счёту (fd-payment-01/02/03/04). DTO для витрины и кабинета покупателя.

export const BuyerBalanceSchema = z.object({
  userId: z.string(),
  amount: z.number(),
  currency: z.string(),
  updatedAt: z.string(),
})
export type BuyerBalance = z.infer<typeof BuyerBalanceSchema>

export const LedgerKindSchema = z.enum(["topup", "spend", "refund", "adjust"])
export type LedgerKind = z.infer<typeof LedgerKindSchema>

export const LedgerEntrySchema = z.object({
  id: z.string(),
  userId: z.string(),
  delta: z.number(),
  kind: LedgerKindSchema,
  refType: z.string().optional(),
  refId: z.string().optional(),
  balanceAfter: z.number(),
  comment: z.string(),
  createdAt: z.string(),
})
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>

export const BalanceTopupSchema = z.object({
  id: z.string(),
  userId: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.enum(["pending", "paid", "declined"]),
  provider: z.string(),
  transactionId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type BalanceTopup = z.infer<typeof BalanceTopupSchema>

// Запрос на пополнение — сумма в валюте площадки.
export const TopupRequestSchema = z.object({ amount: z.number().positive() })
export type TopupRequest = z.infer<typeof TopupRequestSchema>

// Ответ подтверждения пополнения: обновлённый баланс + запись пополнения.
export const TopupConfirmResultSchema = z.object({
  topup: BalanceTopupSchema,
  balance: BuyerBalanceSchema,
})
export type TopupConfirmResult = z.infer<typeof TopupConfirmResultSchema>

export const BillingProfileInputSchema = z.object({
  companyName: z.string().min(1),
  inn: z.string().min(1),
  kpp: z.string().optional(),
  ogrn: z.string().optional(),
  legalAddress: z.string().optional(),
  bankName: z.string().optional(),
  bankAccount: z.string().optional(),
  corrAccount: z.string().optional(),
  bik: z.string().optional(),
  contactName: z.string().optional(),
  contactEmail: z.string().optional(),
  contactPhone: z.string().optional(),
})
export type BillingProfileInput = z.infer<typeof BillingProfileInputSchema>

export const BillingProfileSchema = z.object({
  userId: z.string(),
  companyName: z.string(),
  inn: z.string(),
  kpp: z.string(),
  ogrn: z.string(),
  legalAddress: z.string(),
  bankName: z.string(),
  bankAccount: z.string(),
  corrAccount: z.string(),
  bik: z.string(),
  contactName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type BillingProfile = z.infer<typeof BillingProfileSchema>

export const InvoicePlatformRequisitesSchema = z.object({
  companyName: z.string(),
  inn: z.string(),
  kpp: z.string(),
  ogrn: z.string(),
  legalAddress: z.string(),
  bankName: z.string(),
  bankAccount: z.string(),
  corrAccount: z.string(),
  bik: z.string(),
})
export type InvoicePlatformRequisites = z.infer<typeof InvoicePlatformRequisitesSchema>

export const InvoiceItemSchema = z.object({
  name: z.string(),
  shopName: z.string(),
  qty: z.number(),
  unitPrice: z.number(),
  lineTotal: z.number(),
})
export type InvoiceItem = z.infer<typeof InvoiceItemSchema>

// 54-ФЗ фискальный чек. Оплата проходит виджетом CloudPayments на стороне
// клиента, поэтому сервер отдаёт готовый CustomerReceipt (позиции чека), а
// виджет передаёт его в платёж. CloudPayments сам фискализирует чек, если в
// кабинете подключена онлайн-касса. Формат полей — как ждёт CloudPayments
// (label/price/quantity/amount/vat/method/object).
export const CustomerReceiptItemSchema = z.object({
  label: z.string(),
  price: z.number(),
  quantity: z.number(),
  amount: z.number(),
  /** Ставка НДС: число (0/10/20) или null — «без НДС» (УСН). */
  vat: z.number().nullable(),
  /** Признак способа расчёта (4 — полный расчёт). */
  method: z.number(),
  /** Признак предмета расчёта (1 — товар, 4 — услуга: доставка). */
  object: z.number(),
})
export type CustomerReceiptItem = z.infer<typeof CustomerReceiptItemSchema>

export const CustomerReceiptSchema = z.object({
  Items: z.array(CustomerReceiptItemSchema),
  /** Система налогообложения: 0 — ОСН, 1 — УСН доход, 2 — УСН доход-расход, … */
  taxationSystem: z.number().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  amounts: z
    .object({
      electronic: z.number().optional(),
      advancePayment: z.number().optional(),
      credit: z.number().optional(),
      provision: z.number().optional(),
    })
    .optional(),
})
export type CustomerReceipt = z.infer<typeof CustomerReceiptSchema>

export const InvoiceSchema = z.object({
  orderId: z.string(),
  number: z.string(),
  issuedAt: z.string(),
  currency: z.string(),
  buyer: BillingProfileSchema,
  platform: InvoicePlatformRequisitesSchema,
  items: z.array(InvoiceItemSchema),
  subtotal: z.number(),
  deliveryTotal: z.number(),
  total: z.number(),
})
export type Invoice = z.infer<typeof InvoiceSchema>
